/**
 * Lines that belong to no payout yet.
 *
 * WHY this screen matters most on day one: a freshly connected store has no closed payout, so
 * everything it has earned lives here. It is also where R6 (ORDER_UNSETTLED_OVERDUE) comes from
 * — a line stuck in PENDING past the grace window is money that never arrived.
 */
import type { ReactNode } from "react";
import { api } from "../api";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { LinesTable } from "./PayoutDetail";
import { useAsync } from "../hooks/useAsync";
import { useStore } from "./StoreLayout";

const LIMIT = 200;

export function Unsettled(): ReactNode {
  const store = useStore();
  const state = useAsync(() => api.unsettled(store.id, { limit: LIMIT }), [store.id]);

  if (state.status === "loading") return <Loading label="Loading unsettled lines" />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={state.reload} />;
  const { lines } = state.data;

  return (
    <>
      <h1 className="page-title">Unsettled</h1>
      <p className="page-sub">Transactions that have been recorded but are not part of a closed payout yet.</p>
      {lines.length === 0 ? (
        <EmptyState
          title="Nothing unsettled"
          hint="Every synced transaction for this store already belongs to a payout."
        />
      ) : (
        <>
          <LinesTable lines={lines} />
          {lines.length === LIMIT ? <p className="hint">Showing the {LIMIT} most recent unsettled lines.</p> : null}
        </>
      )}
    </>
  );
}
